import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import {
  useApproveSubmission,
  useBroadcastSubmission,
  useClassroomControl,
  useClearBroadcast,
  useFeedback,
  usePutFeedback,
  useRejectSubmission,
  useRequestPublicPromotion,
} from './useTeacher';
import { submissionBlobUrl } from './teacherApi';
import type { FeedbackRead, RubricCriterion, RubricMark } from './teacherApi';
import './GradeSubmission.css';

// Mirror the backend's controlled rejection vocab. Each entry is a
// kid-friendly explanation paired with the reason code the API
// expects. `needs-revision` is the default — encourages the kid to
// iterate rather than feel returned.
const REJECT_REASONS: { code: string; label: string }[] = [
  { code: 'needs-revision', label: 'Needs revision (let them try again)' },
  { code: 'off-topic', label: 'Off-topic for the assignment' },
  { code: 'personal-info', label: 'Contains personal info that shouldn’t share' },
  { code: 'harassment', label: 'Harassment / unkind' },
  { code: 'copyright', label: 'Copyright issue' },
  { code: 'other', label: 'Other' },
];

/**
 * Rubric grading page — `/teacher/classrooms/:cid/submissions/:sid/grade`.
 *
 * The clarity invariant lives here: every level chip shows BOTH the
 * level name AND its plain-language description. The teacher picks
 * a level by clicking one button; there's nothing to memorize, no
 * dropdown that hides options, no jargon. The kid sees the same
 * descriptions on their submission detail page after grading.
 *
 * State management: we hold a local working copy of marks + comment
 * so the teacher can tick off criteria without round-tripping. Save
 * issues a single PUT (atomic replace) — the server validates every
 * `(criterion_key, level_name)` against the rubric template, so a
 * stale UI gets a 422 with the bad value AND the allowed values.
 *
 * Each criterion supports a per-criterion note ("needs work — but
 * try X next"). The note is its own optional field: the level chip
 * is the *what*; the note is the *how to get to the next level*.
 */
export function GradeSubmission(): JSX.Element {
  const { cid = '', sid = '' } = useParams<{ cid: string; sid: string }>();
  const navigate = useNavigate();
  const { data: user, isPending: userPending } = useCurrentUser();
  const feedback = useFeedback(cid, sid);
  const save = usePutFeedback(cid, sid);
  const approve = useApproveSubmission(cid);
  const reject = useRejectSubmission(cid);
  const promote = useRequestPublicPromotion(cid);

  const [comment, setComment] = useState('');
  const [marks, setMarks] = useState<Record<string, { level_name: string; note_md?: string }>>({});
  const [savedNotice, setSavedNotice] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState<string>('needs-revision');

  // Hydrate the local working copy from the server response. We
  // re-run on every server change so a teacher who saves elsewhere
  // (e.g. another tab) sees the latest state when they come back.
  useEffect(() => {
    if (!feedback.data) return;
    setComment(feedback.data.comment_md);
    const next: Record<string, { level_name: string; note_md?: string }> = {};
    for (const m of feedback.data.marks) {
      next[m.criterion_key] = {
        level_name: m.level_name,
        ...(m.note_md ? { note_md: m.note_md } : {}),
      };
    }
    setMarks(next);
  }, [feedback.data]);

  if (userPending) {
    return <div className="bl-grade" data-testid="grade-loading" />;
  }
  if (!user || (user.role !== 'teacher' && user.role !== 'admin')) {
    return (
      <div className="bl-grade">
        <p className="bl-grade__notice">
          This page is for teachers. <Link to="/studio">Back to the studio →</Link>
        </p>
      </div>
    );
  }

  if (feedback.isPending) {
    return <p className="bl-grade__notice">Loading…</p>;
  }
  if (feedback.isError || !feedback.data) {
    return (
      <p className="bl-grade__notice">
        Couldn&apos;t load the submission.{' '}
        <Link to={`/teacher/classrooms/${cid}`}>← Back to classroom</Link>
      </p>
    );
  }
  const data: FeedbackRead = feedback.data;

  const setLevel = (criterionKey: string, levelName: string): void => {
    setMarks((prev) => {
      const next = { ...prev };
      // Clicking the already-selected level deselects it — lets a
      // teacher unmark a criterion without nuking the whole feedback.
      if (next[criterionKey]?.level_name === levelName) {
        delete next[criterionKey];
        return next;
      }
      next[criterionKey] = {
        level_name: levelName,
        ...(next[criterionKey]?.note_md ? { note_md: next[criterionKey].note_md } : {}),
      };
      return next;
    });
  };

  const setNote = (criterionKey: string, note: string): void => {
    setMarks((prev) => {
      const cur = prev[criterionKey];
      if (!cur) return prev;
      return {
        ...prev,
        [criterionKey]: { level_name: cur.level_name, note_md: note },
      };
    });
  };

  const handleSave = (): void => {
    save.mutate(
      {
        comment_md: comment,
        marks: Object.entries(marks).map(([criterion_key, mark]) => ({
          criterion_key,
          level_name: mark.level_name,
          ...(mark.note_md ? { note_md: mark.note_md } : {}),
        })),
      },
      {
        onSuccess: () => {
          setSavedNotice(true);
          setTimeout(() => setSavedNotice(false), 2500);
        },
      },
    );
  };

  const dirty =
    comment !== data.comment_md || serializeMarks(marks) !== serializeServer(data.marks);

  return (
    <div className="bl-grade">
      <header className="bl-grade__header">
        <Link to={`/teacher/classrooms/${cid}`} className="bl-grade__back">
          ← Back to classroom
        </Link>
        <h1 className="bl-grade__title">{data.project_name}</h1>
        <p className="bl-grade__subtitle">
          by {data.submitter_display_name ?? 'a kid'} · {data.project_kind}
        </p>
        <BroadcastControl classroomId={cid} submissionId={sid} />
      </header>

      {data.project_classes.length > 0 ? (
        <section className="bl-grade__project-panel" aria-label="What the kid made">
          <div className="bl-grade__project-panel-head">
            <h2 className="bl-grade__h2" style={{ marginTop: 0 }}>
              What {data.submitter_display_name ?? 'the kid'} made
            </h2>
            <span className="bl-grade__total">
              {data.project_classes.reduce((acc, c) => acc + c.sample_count, 0)} samples ·{' '}
              {data.project_classes.length} classes
            </span>
          </div>
          <ul className="bl-grade__class-list">
            {data.project_classes.map((c) => (
              <li key={c.class_label} className="bl-grade__class-row">
                {c.sample_blob_id && (c.sample_kind === 'image' || c.sample_kind === 'pose') ? (
                  <img
                    className="bl-grade__class-thumb"
                    src={submissionBlobUrl(cid, sid, c.sample_blob_id)}
                    alt={`${c.class_label} sample`}
                    loading="lazy"
                  />
                ) : c.sample_blob_id && c.sample_kind === 'audio' ? (
                  // Audio samples are 1-second clips. A teacher can't
                  // grade "balanced classes" or "labeled clearly" by
                  // staring at an emoji — they need to hear what the
                  // kid actually recorded. Native <audio> beats a
                  // server-side spectrogram render: zero new backend
                  // deps, and "did the kid say 'happy' or hum?" is
                  // an auditory question, not a visual one.
                  // eslint-disable-next-line jsx-a11y/media-has-caption -- raw kid sample, no captions to attach
                  <audio
                    className="bl-grade__class-audio"
                    src={submissionBlobUrl(cid, sid, c.sample_blob_id)}
                    controls
                    preload="metadata"
                    aria-label={`Play ${c.class_label} sample`}
                  />
                ) : (
                  <div
                    className="bl-grade__class-thumb bl-grade__class-thumb--missing"
                    aria-hidden="true"
                    title="No sample"
                  />
                )}
                <div className="bl-grade__class-meta">
                  <span className="bl-grade__class-name">{c.class_label}</span>
                  <span className="bl-grade__class-count">
                    {c.sample_count} {c.sample_count === 1 ? 'sample' : 'samples'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.rubric.length === 0 ? (
        <div className="bl-grade__empty">
          <p>This submission isn&apos;t from an assignment, so there&apos;s no rubric.</p>
          <p className="bl-grade__empty-hint">
            You can still leave the kid an encouraging note below.
          </p>
        </div>
      ) : (
        <section>
          <h2 className="bl-grade__h2">Rubric</h2>
          <p className="bl-grade__rubric-intro">
            Click a level to mark it. The descriptions are the same ones the kid sees — they know
            exactly what each level means.
          </p>

          <div className="bl-grade__criteria">
            {data.rubric.map((c: RubricCriterion) => {
              const selected = marks[c.key]?.level_name;
              const labelId = `bl-grade__criterion-name--${c.key}`;
              const descId = `bl-grade__criterion-desc--${c.key}`;
              // Roving tabindex: only ONE radio in the group is in
              // the tab order. If there's a selection, that one wins;
              // otherwise the first radio carries focus. Without this
              // every chip is its own tab stop and a teacher
              // arrowing through three rubric criteria has to Tab
              // through all 3 × N levels.
              const focusableIndex = selected ? c.levels.findIndex((l) => l.name === selected) : 0;
              return (
                <div key={c.key} className="bl-grade__criterion">
                  <h3 id={labelId} className="bl-grade__criterion-name">
                    {c.criterion}
                  </h3>
                  <p id={descId} className="bl-grade__criterion-desc">
                    {c.description}
                  </p>

                  <div
                    className="bl-grade__levels"
                    role="radiogroup"
                    aria-labelledby={labelId}
                    aria-describedby={descId}
                  >
                    {c.levels.map((lvl, idx) => (
                      <button
                        type="button"
                        key={lvl.name}
                        role="radio"
                        aria-checked={selected === lvl.name}
                        tabIndex={idx === focusableIndex ? 0 : -1}
                        className={
                          selected === lvl.name
                            ? 'bl-grade__level bl-grade__level--selected'
                            : 'bl-grade__level'
                        }
                        onClick={() => setLevel(c.key, lvl.name)}
                        onKeyDown={(e) => {
                          // Arrow-key navigation — the load-bearing
                          // ARIA pattern for radiogroup. Activates as
                          // it moves so the SR user gets immediate
                          // feedback (matches WAI-ARIA APG).
                          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            const next = (idx + 1) % c.levels.length;
                            setLevel(c.key, c.levels[next]!.name);
                            (
                              e.currentTarget.parentElement?.children[next] as
                                | HTMLElement
                                | undefined
                            )?.focus();
                          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            const prev = (idx - 1 + c.levels.length) % c.levels.length;
                            setLevel(c.key, c.levels[prev]!.name);
                            (
                              e.currentTarget.parentElement?.children[prev] as
                                | HTMLElement
                                | undefined
                            )?.focus();
                          } else if (e.key === ' ' || e.key === 'Enter') {
                            // Space/Enter activates without moving.
                            // Buttons handle this natively via click,
                            // but role="radio" callers expect it.
                            e.preventDefault();
                            setLevel(c.key, lvl.name);
                          }
                        }}
                      >
                        <span className="bl-grade__level-name">{lvl.name}</span>
                        <span className="bl-grade__level-desc">{lvl.description}</span>
                      </button>
                    ))}
                  </div>

                  {selected ? (
                    <textarea
                      className="bl-grade__note"
                      placeholder="Optional note for the kid (e.g. 'try varying your angle next time')"
                      value={marks[c.key]?.note_md ?? ''}
                      onChange={(e) => setNote(c.key, e.target.value)}
                      maxLength={4000}
                      rows={2}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="bl-grade__comment-section">
        <h2 className="bl-grade__h2">Overall comment</h2>
        <p className="bl-grade__rubric-intro">
          One paragraph the kid sees on their submission. Encouragement + one specific thing they
          did well goes a long way.
        </p>
        <textarea
          className="bl-grade__comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={20_000}
          rows={5}
          placeholder="Avery — strong work on…"
        />
      </section>

      <div className="bl-grade__save-bar">
        {save.error instanceof Error ? (
          <p className="bl-grade__error">{save.error.message}</p>
        ) : null}
        {savedNotice ? (
          <p className="bl-grade__saved">Saved ✓</p>
        ) : (
          <p className="bl-grade__hint">{dirty ? 'Unsaved changes' : 'Up to date'}</p>
        )}
        <button
          type="button"
          className="bl-grade__btn bl-grade__btn--secondary"
          onClick={handleSave}
          disabled={save.isPending || !dirty}
        >
          {save.isPending ? 'Saving…' : 'Save feedback'}
        </button>
      </div>

      {data.submission_status === 'awaiting_teacher_review' ? (
        <section className="bl-grade__decision">
          <h2 className="bl-grade__h2">Decision</h2>
          <p className="bl-grade__rubric-intro">
            Your rubric marks + comment save above. The kid sees them either way. Approving makes
            the submission visible to the class; returning sends them back to revise.
          </p>
          {!rejecting ? (
            <div className="bl-grade__decision-actions">
              <button
                type="button"
                className="bl-grade__btn bl-grade__btn--reject"
                onClick={() => setRejecting(true)}
                disabled={approve.isPending || reject.isPending}
              >
                Return for revision…
              </button>
              <button
                type="button"
                className="bl-grade__btn bl-grade__btn--approve"
                onClick={() =>
                  approve.mutate(sid, {
                    onSuccess: () => navigate(`/teacher/classrooms/${cid}`),
                  })
                }
                disabled={approve.isPending || reject.isPending}
              >
                {approve.isPending ? 'Approving…' : 'Approve & post to class'}
              </button>
            </div>
          ) : (
            <div className="bl-grade__reject-panel">
              <label className="bl-grade__reject-label">
                <span>Reason</span>
                <select
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="bl-grade__reject-select"
                >
                  {REJECT_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              <p className="bl-grade__rubric-intro">
                Make sure your overall comment above explains what to fix — that&apos;s what the kid
                sees in their feedback.
              </p>
              <div className="bl-grade__decision-actions">
                <button
                  type="button"
                  className="bl-grade__btn bl-grade__btn--secondary"
                  onClick={() => setRejecting(false)}
                  disabled={reject.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bl-grade__btn bl-grade__btn--reject"
                  onClick={() =>
                    reject.mutate(
                      { submissionId: sid, reason: rejectReason },
                      {
                        onSuccess: () => navigate(`/teacher/classrooms/${cid}`),
                      },
                    )
                  }
                  disabled={reject.isPending}
                >
                  {reject.isPending ? 'Returning…' : 'Confirm return'}
                </button>
              </div>
              {reject.error instanceof Error ? (
                <p className="bl-grade__error">{reject.error.message}</p>
              ) : null}
            </div>
          )}
          {approve.error instanceof Error ? (
            <p className="bl-grade__error">{approve.error.message}</p>
          ) : null}
        </section>
      ) : (
        <p className="bl-grade__hint" style={{ marginTop: 24 }}>
          Submission status: <strong>{data.submission_status}</strong>. Your feedback is visible to
          the kid regardless.
        </p>
      )}

      {data.submission_status === 'approved' && data.submission_visibility === 'class_only' ? (
        <section className="bl-grade__decision">
          <h2 className="bl-grade__h2">Share beyond the class?</h2>
          <p className="bl-grade__rubric-intro">
            This work is approved for your class. To put it on the public gallery — outside the
            school — the kid&apos;s grown-up at home has to say yes too.
          </p>
          {data.submitter_has_parent_contact ? (
            <div className="bl-grade__decision-actions">
              {promote.isSuccess ? (
                <div style={{ flex: 1 }}>
                  <p className="bl-grade__saved">Sent ✓</p>
                  {promote.data.link ? (
                    <p className="bl-grade__hint">
                      Dev link: <code style={{ fontSize: 11 }}>{promote.data.link}</code>
                    </p>
                  ) : null}
                </div>
              ) : (
                <button
                  type="button"
                  className="bl-grade__btn"
                  onClick={() => promote.mutate(sid)}
                  disabled={promote.isPending}
                >
                  {promote.isPending ? 'Sending…' : 'Email the parent for permission'}
                </button>
              )}
            </div>
          ) : (
            <p className="bl-grade__hint">
              No parent contact email on file for {data.submitter_display_name ?? 'this kid'}. Add
              one from the roster tab to enable this.
            </p>
          )}
          {promote.error instanceof Error ? (
            <p className="bl-grade__error">{promote.error.message}</p>
          ) : null}
        </section>
      ) : data.submission_visibility === 'public' ? (
        <p className="bl-grade__hint" style={{ marginTop: 24 }}>
          ✓ This submission is in the public gallery.
        </p>
      ) : null}
    </div>
  );
}

function serializeMarks(m: Record<string, { level_name: string; note_md?: string }>): string {
  const sorted = Object.keys(m).sort();
  return JSON.stringify(
    sorted.map((k) => ({
      criterion_key: k,
      level_name: m[k]?.level_name,
      note_md: m[k]?.note_md ?? '',
    })),
  );
}

function serializeServer(marks: RubricMark[]): string {
  const sorted = [...marks].sort((a, b) => a.criterion_key.localeCompare(b.criterion_key));
  return JSON.stringify(
    sorted.map((m) => ({
      criterion_key: m.criterion_key,
      level_name: m.level_name,
      note_md: m.note_md ?? '',
    })),
  );
}

/**
 * The teacher's "Broadcast to class" button — Phase 4.5b.14 spotlight.
 *
 * Lives on the grading page because that's where the teacher is
 * already looking at one kid's project. One click flips a flag the
 * kid studios poll for; every kid in the room sees a banner pointing
 * at this submission within ~5 seconds.
 *
 * The button is stateful: shows "Broadcasting…" while the request is
 * in flight, "On the board ✓" while this submission is the active
 * spotlight, and "Broadcast to class" otherwise. Only one submission
 * can be broadcast at a time across the classroom.
 */
function BroadcastControl({
  classroomId,
  submissionId,
}: {
  classroomId: string;
  submissionId: string;
}): JSX.Element {
  const control = useClassroomControl(classroomId);
  const broadcast = useBroadcastSubmission(classroomId);
  const clear = useClearBroadcast(classroomId);
  const live = control.data?.broadcast?.submission_id === submissionId;
  const otherLive = Boolean(
    control.data?.broadcast && control.data.broadcast.submission_id !== submissionId,
  );

  if (live) {
    return (
      <div className="bl-grade__broadcast bl-grade__broadcast--on">
        <span className="bl-grade__broadcast-status" aria-live="polite">
          📡 On the board — every kid in the class sees this.
        </span>
        <button
          type="button"
          className="bl-grade__btn bl-grade__btn--secondary"
          onClick={() => clear.mutate()}
          disabled={clear.isPending}
        >
          {clear.isPending ? 'Clearing…' : 'Stop broadcast'}
        </button>
      </div>
    );
  }

  return (
    <div className="bl-grade__broadcast">
      <button
        type="button"
        className="bl-grade__btn bl-grade__btn--secondary"
        onClick={() => broadcast.mutate(submissionId)}
        disabled={broadcast.isPending}
        title="Show this kid's project to everyone in the class. Replaces any current broadcast."
      >
        {broadcast.isPending ? 'Broadcasting…' : '📡 Broadcast to class'}
      </button>
      {otherLive ? (
        <span className="bl-grade__broadcast-hint">
          Another submission is currently on the board — broadcasting this one will replace it.
        </span>
      ) : null}
    </div>
  );
}
